import React, { useEffect, useState } from "react";

import { Modal } from "antd";
import ctx from "classnames";
import { useSelector } from "react-redux";

import { selectQuiz } from "../../features/evaluation/evaluationsSlice";
import { ILesson } from "../../features/lesson/lessonSlice";
import { CogIcon } from "../common/CogIcon";
import { VideoPreviewForLink } from "./VideoPreviewForLink";

import styles from "./linkQuizToVideo.module.scss";
import { getTranslations } from "../../features/translations/translationsUtils";

interface LinkQuizToVideoProps {
  open: boolean;
  lesson: ILesson;
  quizId?: string;
  videoId?: string;
  setNewLesson: React.Dispatch<React.SetStateAction<ILesson>>;
  onClose(): void;
}

export const LinkQuizToVideo: React.FunctionComponent<LinkQuizToVideoProps> = ({
  lesson,
  open,
  quizId,
  videoId,
  setNewLesson,
  onClose,
}) => {
  const [changedLesson, setChangedLesson] = useState<ILesson>();
  const [progress, setProgress] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(true);
  const quiz = useSelector(selectQuiz(quizId || ""));
  const { media = [] } = lesson;
  const [video] = media;

  useEffect(() => {
    if (lesson) {
      setChangedLesson(lesson);
    }
  }, [lesson]);

  if (!quizId || !quiz || !videoId || !video || !video.videoInfo || !lesson) {
    return null;
  }

  const { duration } = video.videoInfo;

  const removeLink = (questionId: string) => () => {
    if (!changedLesson) {
      return;
    }
    const newLesson = { ...changedLesson };
    newLesson.quizCuePoints = !newLesson.quizCuePoints
      ? []
      : [...newLesson.quizCuePoints];
    const index = newLesson.quizCuePoints.findIndex(
      (quizCuePoint) => questionId === quizCuePoint.questionId
    );
    if (index === -1) {
      return;
    }
    newLesson.quizCuePoints.splice(index, 1);
    setChangedLesson(newLesson as ILesson);
  };

  const linkToTime = (questionId: string) => () => {
    if (!isPaused || !changedLesson) {
      return;
    }
    const newLesson = { ...changedLesson };
    newLesson.quizCuePoints = !newLesson.quizCuePoints
      ? []
      : [...newLesson.quizCuePoints];
    const index = newLesson.quizCuePoints.findIndex(
      (quizCuePoint) => questionId === quizCuePoint.questionId
    );
    if (index === -1) {
      newLesson.quizCuePoints.push({
        questionId,
        time: progress * duration,
      });
    } else {
      newLesson.quizCuePoints[index] = {
        questionId,
        time: progress * duration,
      };
    }
    setChangedLesson(newLesson as ILesson);
  };

  return (
    <Modal
      open={open}
      title={<div className={styles.title}>
        {getTranslations('LESSON_LINK_QUIZ')}
        <div className={styles.subTitle}>
        {getTranslations('LESSON_LINK_QUIZ_INFO')}
        </div>
      </div>}
      className={styles.modal}
      onCancel={() => {
        onClose();
      }}
      onOk={() => {
        if (changedLesson) {
          setNewLesson(changedLesson);
        }
        onClose();
      }}
      cancelButtonProps={{
        type: 'default',
        size: 'large'
      }}
      okText={getTranslations('LESSON_LINK')}
      cancelText={getTranslations('LESSON_CANCEL_LINK')}
      okButtonProps={{
        type: 'primary',
        size: 'large',
        disabled:
          !changedLesson?.quizCuePoints ||
          changedLesson?.quizCuePoints.length < quiz.questions.length,
      }}
    >
      <VideoPreviewForLink
        setProgress={setProgress}
        progress={progress}
        onChangeStatus={(isPaused) => {
          setIsPaused(isPaused);
        }}
        duration={duration}
        quizCuePoints={changedLesson?.quizCuePoints || []}
        videoUrl={videoId}
      />
      <div className={styles.innerContainer}>
        <div className={styles.divider} />
        <div className={styles.questions}>
          {quiz.questions.map((question) => {
            const quizCuePoint = changedLesson?.quizCuePoints?.find(
              (quizCuePoint) => quizCuePoint.questionId === question._id
            );
            const hours = quizCuePoint?.time
              ? Math.floor(quizCuePoint?.time / 60)
              : 0;
            const seconds = quizCuePoint?.time
              ? Math.floor(quizCuePoint?.time % 60)
              : 0;
            const cuePointSet = quizCuePoint?.time !== undefined;
            const questionIconColor = cuePointSet ? 'var(--tertiary-text-button-color)' : '#686F84';
            return (
              <div className={styles.question} key={question._id}>
                <div className={ctx(styles.title, { [styles.cueSet]: cuePointSet })}>
                  <div className={styles.innerTitle}>
                    <CogIcon color={questionIconColor} className={styles.icon} icon="test" />
                    {question.title}
                  </div>
                </div>
                {quizCuePoint?.time !== undefined ? (
                  <div className={styles.time}>
                    <CogIcon className={styles.icon} icon="clock" />
                    {("0" + hours).slice(-2)}:{("0" + seconds).slice(-2)}
                    <span onClick={removeLink(question._id || "")}>
                      <CogIcon
                        className={ctx(styles.icon, styles.delete)}
                        icon="Cross"
                        color="#D65F5C"
                      />
                    </span>
                  </div>
                ) : (
                  <span
                    className={ctx(styles.link, {
                      [styles.disabled]: !isPaused,
                    })}
                    onClick={linkToTime(question._id || "")}
                  >
                    {getTranslations('LESSON_LINK')}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};
